import React, { useContext, useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { Can } from "../Can";
import { makeStyles } from "@material-ui/core/styles";
import {
    IconButton, Dialog, DialogTitle, DialogActions,
    List, ListItem, ListItemIcon, ListItemText,
    Switch, Divider, Typography,
} from "@material-ui/core";

import {
    DeviceHubOutlined, History, MoreVert, PictureAsPdf, Replay,
    SwapHorizOutlined, Close, DeleteOutline, SmartToy,
} from "@material-ui/icons";
import { v4 as uuidv4 } from "uuid";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import Tooltip from '@material-ui/core/Tooltip';
import ConfirmationModal from "../ConfirmationModal";

import Button from '@material-ui/core/Button';
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";

import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import UndoIcon from '@material-ui/icons/Undo';
import CallIcon from '@mui/icons-material/Call';
import LabelIcon from "@material-ui/icons/Label";

import ScheduleModal from "../ScheduleModal";
import ShowTicketOpen from "../ShowTicketOpenModal";
import { toast } from "react-toastify";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import ShowTicketLogModal from "../ShowTicketLogModal";
import TicketMessagesDialog from "../TicketMessagesDialog";
import { useTheme } from "@material-ui/styles";
import TicketTagsKanbanModal from "../TicketTagsKanbanModal";

const useStyles = makeStyles(theme => ({
    actionButtons: {
        marginRight: 6,
        flex: "none",
        alignSelf: "center",
        marginLeft: "auto",
        display: "flex",
        alignItems: "center",
        gap: 2,
    },
    iconBtn: {
        padding: 6,
        color: theme.mode === "light" ? '#0872b9' : '#FFF',
    },
    moreBtn: {
        padding: 6,
    },
    // Modal de ações
    menuDialog: {
        "& .MuiDialog-paper": {
            borderRadius: 16,
            minWidth: 300,
            maxWidth: 380,
        },
    },
    menuTitle: {
        padding: "16px 20px 8px",
        "& h2": { fontWeight: 700, fontSize: 15 },
    },
    menuList: {
        padding: "0 8px 8px",
    },
    menuItem: {
        borderRadius: 10,
        padding: "10px 12px",
        marginBottom: 2,
        cursor: "pointer",
        "&:hover": { backgroundColor: theme.palette.action.hover },
    },
    menuItemIcon: {
        minWidth: 38,
        color: theme.palette.text.secondary,
    },
    menuItemDanger: {
        borderRadius: 10,
        padding: "10px 12px",
        marginBottom: 2,
        cursor: "pointer",
        color: "#ef4444",
        "&:hover": { backgroundColor: "#fef2f2" },
        "& $menuItemIcon": { color: "#ef4444" },
    },
    switchRow: {
        borderRadius: 10,
        padding: "6px 12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    // WAVoIP iframe
    iframePaper: {
        width: 600,
        height: 800,
        maxWidth: "none",
        maxHeight: "none",
        borderRadius: 12,
    },
    iframe: {
        width: "100%",
        height: "100%",
        border: "none",
    },
}));

const TicketActionButtonsCustom = ({ ticket }) => {
    const classes = useStyles();
    const theme = useTheme();
    const history = useHistory();
    const [isMounted, setIsMounted] = useState(true);
    const [loading, setLoading] = useState(false);
    const { user } = useContext(AuthContext);
    const { setCurrentTicket, setTabOpen } = useContext(TicketsContext);

    // dialogs
    const [evalOpen, setEvalOpen] = useState(false);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
    const [acceptTicketWithouSelectQueueOpen, setAcceptTicketWithouSelectQueueOpen] = useState(false);
    const [showTicketLogOpen, setShowTicketLogOpen] = useState(false);
    const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
    const [openTagsKanbanModal, setOpenTagsKanbanModal] = useState(false);
    const [openAlert, setOpenAlert] = useState(false);
    const [userTicketOpen, setUserTicketOpen] = useState("");
    const [queueTicketOpen, setQueueTicketOpen] = useState("");

    // MoreVert modal
    const [actionsModalOpen, setActionsModalOpen] = useState(false);

    // WAVoIP
    const [openModal, setOpenModal] = useState(false);
    const [wavoipUrl, setWavoipUrl] = useState("");

    const [disableBot, setDisableBot] = useState(ticket.contact.disableBot);
    const [showSchedules, setShowSchedules] = useState(false);
    const [enableIntegration, setEnableIntegration] = useState(ticket.useIntegration);
    const [isEvaluationEnabled, setIsEvaluationEnabled] = useState(false);

    const { get: getSetting } = useCompanySettings();
    const { getPlanCompany } = usePlans();

    useEffect(() => {
        fetchData();
        return () => { setIsMounted(false); };
    }, []);

    const fetchData = async () => {
        const companyId = user.companyId;
        const planConfigs = await getPlanCompany(undefined, companyId);
        setShowSchedules(planConfigs.plan.useSchedules);
        setDisableBot(ticket.contact.disableBot);
        try {
            const evaluationSetting = await getSetting({ column: "userRating" });
            setIsEvaluationEnabled(evaluationSetting?.userRating === "enabled");
        } catch {
            setIsEvaluationEnabled(false);
        }
    };

    const shouldShowCloseModal = isEvaluationEnabled;

    const handleClickOpen = async (e) => {
        setActionsModalOpen(false);
        const setting = await getSetting({ column: "requiredTag" });
        if (setting?.requiredTag === "enabled") {
            try {
                const contactTags = await api.get(`/contactTags/${ticket.contact.id}`);
                if (!contactTags.data.tags) {
                    toast.warning(i18n.t("messagesList.header.buttons.requiredTag"));
                    return;
                }
            } catch (err) { toastError(err); return; }
        }
        if (shouldShowCloseModal) {
            setEvalOpen(true);
        } else {
            handleUpdateTicketStatus(e, "closed", user?.id);
        }
    };

    const handleOpenWavoipCall = async () => {
        setActionsModalOpen(false);
        try {
            const { data } = await api.get(`/tickets/${ticket.id}`);
            const token = data?.whatsapp?.wavoip;
            const phone = data?.contact?.number?.replace(/\D/g, "");
            const name = data?.contact?.name;
            if (!token || !phone) { toastError("Token ou número não disponível."); return; }
            const url = `https://app.wavoip.com/call?token=${token}&phone=${phone}&name=${name}&start_if_ready=true&close_after_call=true`;
            setWavoipUrl(url);
            setOpenModal(true);
        } catch (err) { toastError("Erro ao buscar dados do ticket."); }
    };

    const handleCloseAlert = () => { setOpenAlert(false); setLoading(false); };

    const handleEnableIntegration = async () => {
        setLoading(true);
        try {
            await api.put(`/tickets/${ticket.id}`, { useIntegration: !enableIntegration });
            setEnableIntegration(!enableIntegration);
        } catch (err) { toastError(err); }
        finally { setLoading(false); }
    };

    const handleContactToggleDisableBot = async () => {
        const { id } = ticket.contact;
        try {
            const { data } = await api.put(`/contacts/toggleDisableBot/${id}`);
            ticket.contact.disableBot = data.disableBot;
            setDisableBot(data.disableBot);
        } catch (err) { toastError(err); }
    };

    const handleResetFlow = async () => {
        setActionsModalOpen(false);
        try {
            await api.post(`/tickets/${ticket.id}/reset-flow`);
            toast.success("Fluxo reiniciado. A pr\u00f3xima mensagem do cliente come\u00e7a do zero.");
        } catch (err) {
            toastError(err);
        }
    };

    const handleCloseTransferTicketModal = () => { setTransferTicketModalOpen(false); };

    const handleDeleteTicket = async () => {
        try {
            await api.delete(`/tickets/${ticket.id}`);
            history.push("/atendimentos");
        } catch (err) { toastError(err); }
    };

    const handleSendMessage = async (id) => {
        try {
            const setting = await getSetting({ column: "greetingAcceptedMessage" });
            const msg = `${setting.greetingAcceptedMessage}`;
            await api.post(`/messages/${id}`, { read: 1, fromMe: true, mediaUrl: "", body: msg.trim() });
        } catch (err) { toastError(err); }
    };

    const handleUpdateTicketStatus = async (e, status, userId) => {
        setLoading(true);
        try {
            await api.put(`/tickets/${ticket.id}`, { status, userId: userId || null });
            let setting;
            try { setting = await getSetting({ column: "sendGreetingAccepted" }); } catch {}
            if (setting?.sendGreetingAccepted === "enabled" && (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled") && ticket.status === "pending") {
                handleSendMessage(ticket.id);
            }
            setLoading(false);
            if (status === "open" || status === "group") {
                setCurrentTicket({ ...ticket, code: "#" + status });
                setTimeout(() => { history.push('/tickets'); }, 0);
                setTimeout(() => { history.push(`/tickets/${ticket.uuid}`); setTabOpen(status); }, 10);
            } else {
                setCurrentTicket({ id: null, code: null });
                history.push("/atendimentos");
            }
        } catch (err) { setLoading(false); toastError(err); }
    };

    const handleCloseTicketWithoutFarewellMsg = async () => {
        setLoading(true);
        try {
            await api.put(`/tickets/${ticket.id}`, { status: "closed", userId: user?.id || null, sendFarewellMessage: false, amountUsedBotQueues: 0 });
            setLoading(false);
            history.push("/atendimentos");
        } catch (err) { setLoading(false); toastError(err); }
    };

    const handleAcepptTicket = async (id) => {
        setLoading(true);
        try {
            const otherTicket = await api.put(`/tickets/${id}`, {
                status: ticket.isGroup && ticket.channel === "whatsapp" ? "group" : "open",
                userId: user?.id,
            });
            if (otherTicket.data.id !== ticket.id) {
                if (otherTicket.data.userId !== user?.id) {
                    setOpenAlert(true);
                    setUserTicketOpen(otherTicket.data.user?.name || "Sem usuário");
                    setQueueTicketOpen(otherTicket.data.queue?.name || "Sem fila");
                    setTabOpen(ticket.isGroup ? "group" : "open");
                } else {
                    setLoading(false);
                    history.push('/tickets');
                    setTimeout(() => { history.push(`/tickets/${ticket.uuid}`); setTabOpen(ticket.isGroup ? "group" : "open"); }, 1000);
                }
            } else {
                let setting;
                try { setting = await getSetting({ column: "sendGreetingAccepted" }); } catch {}
                if (setting?.sendGreetingAccepted === "enabled" && (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled")) {
                    handleSendMessage(ticket.id);
                }
                setLoading(false);
                history.push('/tickets');
                setTimeout(() => { history.push(`/tickets/${ticket.uuid}`); setTabOpen(ticket.isGroup ? "group" : "open"); }, 1000);
            }
        } catch (err) { setLoading(false); toastError(err); }
    };

    const isOpenOrGroup = ticket.status === "open" || ticket.status === "group";

    return (
        <>
            {/* Modais secundários */}
            {openAlert && (
                <ShowTicketOpen isOpen={openAlert} handleClose={handleCloseAlert} user={userTicketOpen} queue={queueTicketOpen} />
            )}
            {acceptTicketWithouSelectQueueOpen && (
                <AcceptTicketWithouSelectQueue
                    modalOpen={acceptTicketWithouSelectQueueOpen}
                    onClose={() => setAcceptTicketWithouSelectQueueOpen(false)}
                    ticketId={ticket.id}
                    ticket={ticket}
                />
            )}
            {showTicketLogOpen && (
                <ShowTicketLogModal isOpen={showTicketLogOpen} handleClose={() => setShowTicketLogOpen(false)} ticketId={ticket.id} />
            )}
            {openTicketMessageDialog && (
                <TicketMessagesDialog open={openTicketMessageDialog} handleClose={() => setOpenTicketMessageDialog(false)} ticketId={ticket.id} />
            )}
            {confirmationOpen && (
                <ConfirmationModal
                    title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")} #${ticket.id}?`}
                    open={confirmationOpen}
                    onClose={setConfirmationOpen}
                    onConfirm={handleDeleteTicket}
                >
                    {i18n.t("ticketOptionsMenu.confirmationModal.message")}
                </ConfirmationModal>
            )}
            {transferTicketModalOpen && (
                <TransferTicketModalCustom
                    modalOpen={transferTicketModalOpen}
                    onClose={handleCloseTransferTicketModal}
                    ticketid={ticket.id}
                    ticket={ticket}
                />
            )}
            <TicketTagsKanbanModal
                open={openTagsKanbanModal}
                onClose={() => setOpenTagsKanbanModal(false)}
                contact={ticket.contact}
                ticket={ticket}
            />

            {/* Botões visíveis no header */}
            <div className={classes.actionButtons}>
                {ticket.status === "closed" && (ticket.queueId === null || ticket.queueId === undefined) && (
                    <ButtonWithSpinner loading={loading} startIcon={<Replay />} size="small" onClick={() => setAcceptTicketWithouSelectQueueOpen(true)}>
                        {i18n.t("messagesList.header.buttons.reopen")}
                    </ButtonWithSpinner>
                )}
                {ticket.status === "closed" && ticket.queueId !== null && (
                    <ButtonWithSpinner startIcon={<Replay />} loading={loading} onClick={() => handleAcepptTicket(ticket.id)}>
                        {i18n.t("messagesList.header.buttons.reopen")}
                    </ButtonWithSpinner>
                )}
                {ticket.status === "pending" && (ticket.queueId === null || ticket.queueId === undefined) && (
                    <ButtonWithSpinner loading={loading} size="small" variant="contained" onClick={() => setAcceptTicketWithouSelectQueueOpen(true)}>
                        {i18n.t("messagesList.header.buttons.accept")}
                    </ButtonWithSpinner>
                )}
                {ticket.status === "pending" && ticket.queueId !== null && (
                    <ButtonWithSpinner loading={loading} size="small" variant="contained" onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}>
                        {i18n.t("messagesList.header.buttons.accept")}
                    </ButtonWithSpinner>
                )}

                {isOpenOrGroup && (
                    <Tooltip title={i18n.t("messagesList.header.buttons.resolve")}>
                        <IconButton className={classes.iconBtn} onClick={handleClickOpen}>
                            <HighlightOffIcon />
                        </IconButton>
                    </Tooltip>
                )}

                {/* Botão hamburguer — abre modal de ações */}
                <IconButton className={classes.moreBtn} onClick={() => setActionsModalOpen(true)}>
                    <MoreVert style={{ fontSize: 20 }} />
                </IconButton>
            </div>

            {/* Modal de ações (hamburguer) */}
            <Dialog open={actionsModalOpen} onClose={() => setActionsModalOpen(false)} className={classes.menuDialog}>
                <DialogTitle className={classes.menuTitle}>Ações do atendimento</DialogTitle>
                <List className={classes.menuList} disablePadding>
                    {isOpenOrGroup && (
                        <>
                            <ListItem className={classes.menuItem} onClick={handleOpenWavoipCall}>
                                <ListItemIcon className={classes.menuItemIcon}><CallIcon /></ListItemIcon>
                                <ListItemText primary="Iniciar chamada" />
                            </ListItem>

                            <ListItem className={classes.menuItem} onClick={() => { setActionsModalOpen(false); setOpenTagsKanbanModal(true); }}>
                                <ListItemIcon className={classes.menuItemIcon}><LabelIcon /></ListItemIcon>
                                <ListItemText primary="Tags & Kanban" />
                            </ListItem>

                            <ListItem className={classes.menuItem} onClick={() => { setActionsModalOpen(false); handleUpdateTicketStatus(null, "pending", null); }}>
                                <ListItemIcon className={classes.menuItemIcon}><UndoIcon /></ListItemIcon>
                                <ListItemText primary={i18n.t("tickets.buttons.returnQueue")} />
                            </ListItem>

                            <ListItem className={classes.menuItem} onClick={handleResetFlow}>
                                <ListItemIcon className={classes.menuItemIcon}><Replay /></ListItemIcon>
                                <ListItemText primary="Reiniciar fluxo" />
                            </ListItem>

                            <ListItem className={classes.menuItem} onClick={() => { setActionsModalOpen(false); setTransferTicketModalOpen(true); }}>
                                <ListItemIcon className={classes.menuItemIcon}><SwapHorizOutlined /></ListItemIcon>
                                <ListItemText primary="Transferir ticket" />
                            </ListItem>

                            <Divider style={{ margin: "4px 0" }} />

                            <div className={classes.switchRow}>
                                <Typography style={{ fontSize: 14 }}>Pausar bot do contato</Typography>
                                <Switch size="small" checked={disableBot} onChange={handleContactToggleDisableBot} />
                            </div>

                            <div className={classes.switchRow}>
                                <Typography style={{ fontSize: 14 }}>
                                    {enableIntegration ? i18n.t("messagesList.header.buttons.disableIntegration") : i18n.t("messagesList.header.buttons.enableIntegration")}
                                </Typography>
                                <Switch size="small" checked={enableIntegration} onChange={handleEnableIntegration} />
                            </div>

                            <Divider style={{ margin: "4px 0" }} />
                        </>
                    )}

                    <ListItem className={classes.menuItem} onClick={() => { setActionsModalOpen(false); setShowTicketLogOpen(true); }}>
                        <ListItemIcon className={classes.menuItemIcon}><History /></ListItemIcon>
                        <ListItemText primary={i18n.t("messagesList.header.buttons.logTicket")} />
                    </ListItem>

                    <ListItem className={classes.menuItem} onClick={() => { setActionsModalOpen(false); setOpenTicketMessageDialog(true); }}>
                        <ListItemIcon className={classes.menuItemIcon}><PictureAsPdf /></ListItemIcon>
                        <ListItemText primary={i18n.t("ticketsList.buttons.exportAsPDF")} />
                    </ListItem>

                    {["administrador", "gerente"].includes(user?.userType) && (
                        <>
                            <Divider style={{ margin: "4px 0" }} />
                            <ListItem className={classes.menuItemDanger} onClick={() => { setActionsModalOpen(false); setConfirmationOpen(true); }}>
                                <ListItemIcon className={classes.menuItemIcon}><DeleteOutline /></ListItemIcon>
                                <ListItemText primary={i18n.t("tickets.buttons.deleteTicket")} />
                            </ListItem>
                        </>
                    )}
                </List>
                <DialogActions style={{ padding: "8px 16px 12px" }}>
                    <Button onClick={() => setActionsModalOpen(false)} style={{ textTransform: "none" }}>Fechar</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de avaliação ao fechar */}
            <Dialog open={evalOpen} onClose={() => setEvalOpen(false)} aria-labelledby="rating-dialog-title">
                <div style={{ padding: "24px 28px 8px" }}>
                    <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>Deseja enviar avaliação?</div>
                    <div style={{ color: "#757575", fontSize: 14 }}>
                        Ao fechar o atendimento, o cliente pode receber uma mensagem de avaliação.
                    </div>
                </div>
                <DialogActions style={{ padding: "12px 20px 16px", gap: 8 }}>
                    <Button onClick={handleCloseTicketWithoutFarewellMsg} variant="outlined" color="default" disabled={loading}>
                        Não, fechar sem avaliação
                    </Button>
                    <Button
                        onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
                        variant="contained"
                        style={{ background: theme.palette.primary.main, color: "white" }}
                        disabled={loading}
                    >
                        Sim, enviar avaliação
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal WAVoIP */}
            <Dialog
                open={openModal}
                onClose={() => setOpenModal(false)}
                maxWidth="md"
                disableBackdropClick
                disableEscapeKeyDown
                disableEnforceFocus
                BackdropProps={{ invisible: true }}
                PaperProps={{ className: classes.iframePaper }}
            >
                <iframe src={wavoipUrl} className={classes.iframe} title="WAVoIP Call" />
                <DialogActions>
                    <Button onClick={() => setOpenModal(false)} color="primary">Fechar</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TicketActionButtonsCustom;
